import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api/client";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast, showSuccessToast } from '../utils/toast';        // Block 233
import { Button, Chip, Input, Textarea, Select, Field, Checkbox, Banner, StatusPill, Avatar } from '../ui';
import './MeetingMode/meeting.css';

// The restyle (v2): people's colours are pastel token NAMES (Avatar and tags take a token name),
// always with ink text. You are the highlight.
const ME_COLOUR = "highlight";
const PEOPLE_COLOURS = ["pastel-lilac", "pastel-orange", "pastel-green", "pastel-pink", "pastel-blue", "pastel-yellow", "pastel-mint", "pastel-coral"];

const PLATFORMS = [
  { id: "inperson",    label: "In person",      icon: "🎙️", desc: "Aidey listens through your device microphone" },
  { id: "zoom",        label: "Zoom",            icon: "💙", desc: "Record your Zoom meeting, then paste the transcript" },
  { id: "teams",       label: "Microsoft Teams", icon: "💜", desc: "Use Teams live transcription, then paste below" },
  { id: "googlemeet",  label: "Google Meet",     icon: "🟢", desc: "Use Meet's captions or notes, then paste below" },
  { id: "other",       label: "Other or paste",  icon: "📋", desc: "Paste any transcript or notes" },
];

// ─── Summary generator ───────────────────────────────────────────────────────
// Pattern-based summary from a transcript. Extracts decisions, topics, action items.
function generateSummary(transcript, allUsers) {
  const sentences = transcript
    .split(/[.!?\n]+/)
    .map(s => s.trim())
    .filter(s => s.length > 10);

  // Action item patterns
  const actionPatterns = [
    /\b(\w[\w\s]+?)\s+(?:will|is going to|needs? to|has to|should|must|is responsible for)\s+(.{8,80})/i,
    /\b(\w[\w\s]+?)\s+to\s+(follow up|send|review|update|check|create|write|prepare|contact|schedule|call|fix|build|share)\b.{0,60}/i,
    /^(?:action(?:\s+item)?|todo|task|follow.?up)[\s:]+(.+)/i,
  ];

  const decisionPatterns = [
    /(?:we|team|everyone)?\s*(?:agreed|decided|confirmed|resolved|approved|going with|will use|chose|selected)\s+(?:to\s+)?(.{8,100})/i,
    /decision[:–]\s*(.{8,100})/i,
  ];

  const actions   = [];
  const decisions = [];
  const topics    = new Set();

  // Extract keywords as topics from frequent nouns
  const wordFreq = {};
  const stopWords = new Set(["the","a","an","is","are","was","were","be","been","have","has","had","that","this","it","we","they","he","she","i","and","or","but","in","on","at","to","for","of","with","about","from","by"]);
  transcript.toLowerCase().match(/\b[a-z]{4,}\b/g)?.forEach(w => {
    if (!stopWords.has(w)) wordFreq[w] = (wordFreq[w] || 0) + 1;
  });
  Object.entries(wordFreq)
    .filter(([, v]) => v >= 2)
    .sort(([,a],[,b]) => b - a)
    .slice(0, 6)
    .forEach(([w]) => topics.add(w.charAt(0).toUpperCase() + w.slice(1)));

  for (const sentence of sentences) {
    let matched = false;
    for (const pat of actionPatterns) {
      const m = sentence.match(pat);
      if (m) {
        const nameHint = m[1]?.trim();
        const actionText = (m[2] || m[1] || sentence).trim();
        const user = allUsers.find(u =>
          nameHint && (u.name.toLowerCase().includes(nameHint.toLowerCase()) ||
          (u.department && u.department.toLowerCase().includes(nameHint.toLowerCase())) ||
          (u.jobTitle && u.jobTitle.toLowerCase().includes(nameHint.toLowerCase())))
        );
        actions.push({ text: actionText, assigneeName: user?.name || nameHint || null, assigneeId: user?.id || null, assigneeDept: null });
        matched = true;
        break;
      }
    }
    if (!matched) {
      for (const pat of decisionPatterns) {
        const m = sentence.match(pat);
        if (m) { decisions.push(m[1].trim()); break; }
      }
    }
  }

  return { actions: actions.slice(0, 20), decisions: decisions.slice(0, 10), topics: [...topics] };
}

// ─── Per-platform setup instructions (for non-developers) ────────────────────
const PLATFORM_GUIDES = {
  zoom: {
    steps: [
      { n: 1, text: "Open Zoom → Settings → Recording", action: { label: "Open Zoom Settings", url: "https://zoom.us/profile/setting" } },
      { n: 2, text: "Under Cloud Recording, enable 'Audio transcript'. This makes Zoom automatically create a text transcript after every recorded meeting." },
      { n: 3, text: "Start your meeting and click Record → Record to the Cloud." },
      { n: 4, text: "After the meeting ends, go to zoom.us → Recordings. Open the meeting and click the .vtt or text transcript file." },
      { n: 5, text: "Copy all the text and paste it into Aidey below." },
    ],
    tip: "Zoom AI Companion (paid plans) can do this automatically. OtterAI (free tier available) also integrates directly with Zoom for auto-transcription.",
  },
  teams: {
    steps: [
      { n: 1, text: "During the meeting, click the three-dot menu (…) → Start transcription. Everyone in the meeting will be notified." },
      { n: 2, text: "Teams will show a live transcript panel on the right side." },
      { n: 3, text: "After the meeting ends, go to the meeting chat or your Teams calendar and open the meeting." },
      { n: 4, text: "Click 'Transcript' → Download .docx or copy the text." },
      { n: 5, text: "Paste the transcript into Aidey below." },
    ],
    tip: "Teams transcription is available on Microsoft 365 Business Basic and above. The meeting organiser must enable it in meeting policies.",
  },
  googlemeet: {
    steps: [
      { n: 1, text: "During the meeting, click Activities (the puzzle-piece icon) → Transcripts → Start transcript." },
      { n: 2, text: "Or use Google Meet's AI notes: click the pencil icon to enable 'Take notes for me' (Workspace accounts)." },
      { n: 3, text: "After the meeting, the transcript is saved automatically to Google Drive as a Google Doc." },
      { n: 4, text: "Open the Doc → Select all (Ctrl/Cmd + A) → Copy → Paste into Aidey below." },
    ],
    tip: "Transcription in Google Meet requires a Google Workspace account (Business Starter or above). Personal Gmail accounts can use a Chrome extension like Otter.ai.",
  },
  other: {
    steps: [
      { n: 1, text: "Get your meeting transcript from whichever tool you use (Otter.ai, Fireflies.ai, your video platform, or your own notes)." },
      { n: 2, text: "Paste it into the text area below." },
    ],
    tip: "Otter.ai (otter.ai) has a free tier that records and transcribes meetings from Zoom, Teams, and Google Meet automatically.",
  },
};

// ─── Browser capability detection (Block 179) ────────────────────────────────
// Live recording uses the Web Speech API, which today is Chrome/Edge only. Detection is honestly
// imperfect: hasWebSpeech is a real feature check (reliable), but isPhone is UA-based and can be
// wrong (spoofed UAs, iPad reporting as desktop). We only use isPhone to choose which WARNING to
// show when recording IS available — it never gates functionality — so a wrong guess just shows a
// slightly less-tailored (still true) message.
const hasWebSpeech = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
const isPhone = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

// ─── Component ────────────────────────────────────────────────────────────────
export default function MeetingRecorder({ onClose, setFilter, onCreateTask }) {
  const { currentUser } = useCurrentUser();

  // Real workspace team members from the backend (not the legacy localStorage
  // userManager, which held stale mock users). The current user is always
  // included so they can be an attendee/assignee even if they're the team owner
  // (who has no teamMember row). Empty for solo users with no team — correct.
  const [allUsers, setAllUsers] = useState(
    currentUser ? [{ id: currentUser.id, name: currentUser.name || "You", email: currentUser.email, color: ME_COLOUR, department: null, jobTitle: null }] : []
  );
  useEffect(() => {
    const palette = PEOPLE_COLOURS;
    let cancelled = false;
    api("/api/team/members")
      .then(res => (res.ok ? res.json() : { members: [] }))
      .then(data => {
        if (cancelled) return;
        const members = (data.members || []).map((m, i) => ({
          id: m.id, name: m.name, email: m.email,
          color: palette[i % palette.length],
          department: m.teamRole || null, jobTitle: m.teamRole || null,
        }));
        if (currentUser && !members.find(u => u.id === currentUser.id)) {
          members.unshift({ id: currentUser.id, name: currentUser.name || "You", email: currentUser.email, color: ME_COLOUR, department: null, jobTitle: null });
        }
        if (members.length) setAllUsers(members);
      })
      // A failed load must not look like "you have no team": say so beside the attendee picker.
      .catch(err => { if (!cancelled) setMembersError(messageFromError(err, "Your team did not load.")); });
    return () => { cancelled = true; };
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [phase, setPhase]         = useState("setup");   // setup | record | review | summary | done
  const [platform, setPlatform]   = useState("inperson");
  const [title, setTitle]         = useState("");
  const [attendeeIds, setAttendeeIds] = useState(currentUser ? [currentUser.id] : []);
  const [transcript, setTranscript]   = useState("");
  const [pastedText, setPastedText]   = useState("");
  const [isListening, setIsListening] = useState(false);
  const [summary, setSummary]         = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [tasksDone, setTasksDone]     = useState(false);
  const [error, setError]             = useState("");
  const [showGuide, setShowGuide]     = useState(false);
  const [interimText, setInterimText] = useState("");
  const [externalInput, setExternalInput]         = useState("");
  const [externalAttendees, setExternalAttendees] = useState([]);
  const [isAnalysing, setIsAnalysing]             = useState(false);

  // Block 179: CRM contact this meeting is with. Default null ("no contact"), USER-PICKED only —
  // never inferred from the transcript. noteId is the Note created by process-transcript, which we
  // re-attach to the chosen contact at save.
  const [contacts, setContacts]     = useState([]);
  const [contactsError, setContactsError] = useState("");
  const [membersError, setMembersError]   = useState("");
  // True when the summary came from the on-device fallback, not Aidey (shown, never silent).
  const [usedFallback, setUsedFallback]   = useState(false);
  const [contactId, setContactId]   = useState(null);
  const [noteId, setNoteId]         = useState(null);

  const recognitionRef = useRef(null);
  const finalTranscript = useRef("");

  // Load CRM contacts for the picker.
  useEffect(() => {
    let cancelled = false;
    api("/api/contacts")
      .then(res => (res.ok ? res.json() : { contacts: [] }))
      .then(data => { if (!cancelled) setContacts(data.contacts || []); })
      // A failed load must not look like "you have no contacts": the picker says so.
      .catch(err => { if (!cancelled) setContactsError(messageFromError(err, "Your contacts did not load.")); });
    return () => { cancelled = true; };
  }, []);

  // ── Web Speech API (in-person only) ────────────────────────────────────────
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError("Live transcription needs Chrome or Edge. For other browsers, please use a video platform and paste the transcript.");
      return;
    }
    const rec = new SR();
    rec.continuous      = true;
    rec.interimResults  = true;
    rec.lang            = "en-GB";
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const text = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          finalTranscript.current += text + " ";
          setTranscript(finalTranscript.current);
        } else {
          interim = text;
        }
      }
      setInterimText(interim);
    };

    rec.onerror = (e) => {
      if (e.error !== "no-speech") setError(`Microphone error: ${e.error}. Check your browser permissions.`);
    };

    // Auto-restart after silence (recognition stops after ~15s of quiet)
    rec.onend = () => {
      if (isListening && recognitionRef.current === rec) {
        try { rec.start(); } catch {}
      }
    };

    rec.start();
    recognitionRef.current = rec;
    setIsListening(true);
  }, [isListening]);

  const stopListening = useCallback(() => {
    setIsListening(false);
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setInterimText("");
  }, []);

  useEffect(() => () => stopListening(), [stopListening]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleAttendee = (id) =>
    setAttendeeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const addExternalAttendee = () => {
    const name = externalInput.trim();
    if (!name) return;
    if (!externalAttendees.find(e => e.name.toLowerCase() === name.toLowerCase())) {
      setExternalAttendees(prev => [...prev, { name, isClient: true }]);
    }
    setExternalInput("");
  };
  const removeExternal = (name) =>
    setExternalAttendees(prev => prev.filter(e => e.name !== name));

  const attendees = attendeeIds.map(id => allUsers.find(u => u.id === id)).filter(Boolean);

  // Block 179: in-person uses live Web Speech text ONLY where it's available; everywhere else
  // (paste platforms, and in-person on a browser without Web Speech) the pasted text is the source.
  const canRecordLive = platform === "inperson" && hasWebSpeech;
  const fullTranscript = canRecordLive ? transcript : pastedText;

  // Block 179: contact picker (used at setup as an optional pre-select, and at the summary/save step
  // as the authoritative choice). "No contact" is the default. Never inferred from the transcript.
  const contactSelect = (label, hint) => (
    <Field label={label} hint={contactsError ? undefined : hint} error={contactsError ? `${contactsError} You can still save without one.` : undefined}>
      <Select
        value={contactId || ""}
        onChange={e => setContactId(e.target.value || null)}
      >
        <option value="">No contact</option>
        {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
      </Select>
    </Field>
  );

  const handleGenerateSummary = async () => {
    const text = fullTranscript.trim();
    if (text.length < 30) { setError("Please add more content before generating a summary."); return; }
    setIsAnalysing(true);
    setError("");
    const allAttendeeNames = [
      ...attendees.map(u => u.name),
      ...externalAttendees.map(e => e.name),
    ];
    let result = null;
    // Block 179: ALL transcripts (live-recorded here, or pasted) go through process-transcript, the
    // single endpoint — same summary, decisions, action items WITH priority, topics, CRM matching,
    // and a saved Note. contactId carries any optional pre-selection; the picker at this step is the
    // authoritative choice, re-applied to the note at save.
    try {
      const res = await api("/api/meetings/google-meet/process-transcript", {
        method: "POST",
        body: JSON.stringify({
          transcriptText: text,
          meetingTitle: title || "Meeting",
          attendees: allAttendeeNames,
          contactId: contactId || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setNoteId(data.noteId || null);
        result = {
          actions: (data.actionItems || []).map(a => ({ text: a.task, assigneeName: a.assignee || null, priority: a.priority || null })),   // none: her usual one at save (lib/learntDefaults)
          decisions: data.decisions || [],
          topics: data.topics || [],
        };
      }
    } catch (err) {
      // Block 233: this was `else if (res.status === 403)` above — unreachable, since
      // api() throws. So hitting the AI usage limit showed NOTHING: the summary
      // silently degraded to the local regex fallback below and the user was never
      // told why it was worse, nor that upgrading would fix it.
      if (err?.status === 403) {
        setError("You've reached your AI usage limit. Upgrade to summarise more meetings.");
      }
    }
    // Fallback: if the endpoint failed (offline / limit), keep the feature working with the local
    // regex summariser. It has no priority/topics and creates no Note — a graceful degradation.
    setUsedFallback(!result);
    if (!result) result = generateSummary(text, allUsers);
    const actionsWithIds = result.actions.map((a, i) => {
      const user = a.assigneeName
        ? allUsers.find(u => u.name.toLowerCase().includes(a.assigneeName.toLowerCase()))
        : null;
      return { ...a, id: i, keep: true, assigneeId: user?.id || null, assigneeName: a.assigneeName || null, priority: a.priority || null };
    });
    setSummary(result);
    setActionItems(actionsWithIds);
    setIsAnalysing(false);
    setPhase("summary");
  };

  const handleCreateTasks = async () => {
    const kept = actionItems.filter(a => a.keep);
    if (kept.length === 0) { setError("No action items selected."); return; }

    const today = new Date().toISOString().slice(0, 10);
    const meetingTitle = title || "Meeting";

    if (onCreateTask) {
      for (const item of kept) {
        const assignIds = item.assigneeIds?.length ? item.assigneeIds
          : item.assigneeId ? [item.assigneeId] : [];
        onCreateTask({
          title: item.text,
          date: today,
          ...(item.priority ? { priority: item.priority } : {}),   // none chosen: her usual one (lib/learntDefaults), else Medium
          taskType: "Meeting",
          assignedTeam: assignIds,
          userId: assignIds[0] || null,
          details: `Action item from: ${meetingTitle}`,
        });
      }
    } else {
      // Persist to the workspace-scoped tasks API (was a localStorage orphan that
      // never reached the DB). onCreateTask is the normal path; this is the fallback.
      for (const item of kept) {
        const assignIds = item.assigneeIds?.length ? item.assigneeIds
          : item.assigneeId ? [item.assigneeId] : [];
        try {
          await api('/api/tasks', {
            method: 'POST',
            body: JSON.stringify({
              title: item.text,
              ...(item.priority ? { priority: item.priority } : {}),   // none chosen: her usual one (lib/learntDefaults), else Medium
              date: today,
              datetime: new Date().toISOString(),
              taskType: "Meeting",
              userId: assignIds[0] || null,
              assignedTeam: assignIds,
              details: `Action item from: ${meetingTitle}`,
            }),
          });
        } catch (err) { showErrorToast(messageFromError(err, "Could not create tasks.")); }
      }
      window.dispatchEvent(new Event("aidey:tasksUpdated"));
    }

    // Block 179: attach the transcript Note (created by process-transcript) to the picked contact.
    // The user's choice wins — this PUT sets Note.contactId to exactly what they selected (or null
    // for "no contact"), overriding whatever was sent at analysis time.
    if (noteId) {
      try {
        await api(`/api/notes/${noteId}`, { method: "PUT", body: JSON.stringify({ contactId: contactId || null }) });
      } catch (_) { /* note stays with its analysis-time contact; non-fatal */ }
    }

    // Store meeting record
    const meetings = (() => { try { return JSON.parse(localStorage.getItem("meetingRecords") || "[]"); } catch { return []; } })();
    meetings.unshift({
      id: `mtg-${Date.now()}`,
      title: meetingTitle,
      platform,
      date: new Date().toISOString(),
      attendeeIds,
      externalAttendees,
      transcript: fullTranscript,
      summary,
      actionItems: kept,
    });
    localStorage.setItem("meetingRecords", JSON.stringify(meetings.slice(0, 50)));

    setTasksDone(true);
    setPhase("done");
  };

  const copySummary = () => {
    const att = attendees.map(u => u.name).join(", ");
    const acts = actionItems.filter(a => a.keep).map(a => `• ${a.assigneeName || "Unassigned"}: ${a.text}`).join("\n");
    const decs = summary?.decisions?.map(d => `• ${d}`).join("\n") || "";
    const text = [
      `Meeting Summary, ${title || "Meeting"}`,
      `Date: ${new Date().toLocaleDateString("en-GB")}`,
      `Attendees: ${att || "—"}`,
      "",
      summary?.decisions?.length ? `KEY DECISIONS\n${decs}` : "",
      acts ? `ACTION ITEMS\n${acts}` : "",
      summary?.topics?.length ? `TOPICS DISCUSSED\n${summary.topics.join(", ")}` : "",
    ].filter(Boolean).join("\n");
    // Say what happened: "Copied" only once the copy has worked, and a plain message when it did not.
    Promise.resolve(navigator.clipboard?.writeText ? navigator.clipboard.writeText(text) : Promise.reject(new Error('no clipboard')))
      .then(() => showSuccessToast('Summary copied.'))
      .catch(() => showErrorToast('Could not copy the summary. Select the text and copy it yourself.'));
  };

  // ── Shared layout (v2): a highlight band, a scrolling body, and the main actions in a bar
  // under it that stays above the keyboard and the safe area on a phone. ──────────────────────
  const shell = ({ title: heading, sub, back, close, body, foot, testId }) => (
    <div className="mtg" data-testid={testId}>
      <header className="mtg__head">
        {back && (
          <Button variant="tertiary" size="sm" className="ui-hit" onClick={back}>
            <span aria-hidden="true">‹</span> Back
          </Button>
        )}
        <div className="mtg__headtext">
          <h2 className="mtg__title">{heading}</h2>
          {sub && <p className="mtg__sub">{sub}</p>}
        </div>
        {close && (
          <button type="button" className="mtg__x" onClick={close} aria-label="Close">
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </header>
      <div className="mtg__body mtg__body--narrow">{body}</div>
      {foot && <footer className="mtg__foot mtg__foot--end">{foot}</footer>}
    </div>
  );

  const errorBanner = error ? <Banner tone="problem">{error}</Banner> : null;

  const guideSteps = (steps) => (
    <ol className="mtg-steps">
      {steps.map(s => (
        <li key={s.n}>
          {s.text}
          {s.action && (
            <>
              {" "}
              <a href={s.action.url} target="_blank" rel="noopener noreferrer" className="mtg-link">
                {s.action.label}
              </a>
            </>
          )}
        </li>
      ))}
    </ol>
  );

  // ── PHASE: SETUP ────────────────────────────────────────────────────────────
  if (phase === "setup") return shell({
    testId: "meeting-recorder-setup",
    title: "Start a meeting",
    sub: "Aidey will record and summarise your meeting, then create tasks for everyone.",
    close: onClose,
    body: (
      <>
        {errorBanner}

        <Field label="Meeting title">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Q2 planning, sprint review, client call" />
        </Field>

        {/* Platform picker */}
        <div className="ui-field" role="group" aria-labelledby="mtg-type-label">
          <span className="ui-field__label" id="mtg-type-label">Meeting type</span>
          <div className="mtg-options">
            {PLATFORMS.map(p => (
              <button key={p.id} type="button" className="mtg-option" aria-pressed={platform === p.id}
                onClick={() => { setPlatform(p.id); setError(""); }}>
                <span className="mtg-option__icon" aria-hidden="true">{p.icon}</span>
                <span className="mtg-option__label">{p.label}</span>
                <span className="mtg-option__desc">{p.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Platform setup guide */}
        {platform !== "inperson" && (
          <div className="mtg-stack">
            <button type="button" className="mtg-disclose" aria-expanded={showGuide} onClick={() => setShowGuide(v => !v)}>
              <span className="mtg-disclose__caret" aria-hidden="true">›</span>
              How to get your transcript from {PLATFORMS.find(p2 => p2.id === platform)?.label}
            </button>
            {showGuide && PLATFORM_GUIDES[platform] && (
              <section className="mtg-card mtg-card--strip" style={{ "--ui-bg": "var(--pastel-blue)" }}>
                {guideSteps(PLATFORM_GUIDES[platform].steps)}
                {PLATFORM_GUIDES[platform].tip && (
                  <Banner tone="notice"><strong>Tip:</strong> {PLATFORM_GUIDES[platform].tip}</Banner>
                )}
              </section>
            )}
          </div>
        )}

        {/* Attendee picker */}
        <div className="ui-field" role="group" aria-labelledby="mtg-attendees-label">
          <span className="ui-field__label" id="mtg-attendees-label">Attendees</span>
          <div className="mtg-chips">
            {allUsers.map(u => {
              const on = attendeeIds.includes(u.id);
              return (
                <Chip key={u.id} selected={on} onClick={() => toggleAttendee(u.id)}>
                  {u.name}
                  {u.department ? ` · ${u.department}` : ""}
                  {u.id === currentUser?.id ? " (you)" : ""}
                </Chip>
              );
            })}
          </div>
          {attendees.length > 0 && (
            <span className="ui-field__hint">
              {attendees.length} attendee{attendees.length !== 1 ? "s" : ""}. Aidey will assign tasks to them from the meeting discussion.
            </span>
          )}
          {membersError && <p className="mtg-problem" role="alert">{membersError} Only you are listed.</p>}
        </div>

        {/* External / client attendees */}
        <div className="ui-field">
          <label className="ui-field__label" htmlFor="mtg-external">External attendees (clients or guests)</label>
          <div className="mtg-row" style={{ flexWrap: "nowrap" }}>
            <Input
              id="mtg-external"
              value={externalInput}
              onChange={e => setExternalInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addExternalAttendee())}
              placeholder="Type a name and press Enter"
            />
            <Button variant="secondary" onClick={addExternalAttendee}>Add</Button>
          </div>
          {externalAttendees.length > 0 && (
            <div className="mtg-chips">
              {externalAttendees.map(e => (
                <span key={e.name} className="mtg-tag" style={{ "--ui-bg": "var(--pastel-orange)" }}>
                  {e.name} <span className="mtg-tag__note">client</span>
                  <button type="button" className="mtg-tag__x ui-hit" onClick={() => removeExternal(e.name)} aria-label={`Remove ${e.name}`}>
                    <span aria-hidden="true">×</span>
                  </button>
                </span>
              ))}
            </div>
          )}
          <span className="ui-field__hint">External attendees are shown in the summary but won't receive task assignments.</span>
        </div>

        {/* Block 179: optional contact pre-select. You can also pick (or change) it at the summary step. */}
        {contactSelect("Who is this meeting with? (optional)", "Links the summary note to a CRM contact. You can also choose this after the meeting.")}
      </>
    ),
    foot: (
      <Button variant="confirm" onClick={() => { setError(""); setPhase("record"); }}>
        {canRecordLive ? "Start recording" : "Continue to transcript"}
      </Button>
    ),
  });

  // ── PHASE: RECORD ───────────────────────────────────────────────────────────
  if (phase === "record") return shell({
    testId: "meeting-recorder-record",
    title: canRecordLive ? "Record the meeting" : "Paste a transcript",
    back: () => setPhase("setup"),
    body: canRecordLive ? (
      <>
        {errorBanner}
        {/* Block 179: phone warning: recording dies if the screen locks or the app is switched. */}
        {isPhone && (
          <Banner tone="notice">Keep this screen open while you record. If your phone locks or you switch apps, recording will stop.</Banner>
        )}
        {/* Live recording UI */}
        <div className={`mtg-box${isListening ? " mtg-box--live" : ""}`}>
          <div className="mtg-pill-row">
            {isListening
              ? <span className="mtg-rec" role="status"><span className="mtg-rec__dot" aria-hidden="true" />Listening</span>
              : <span className="mtg-rec mtg-rec--paused" role="status"><span className="mtg-rec__dot" aria-hidden="true" />{transcript ? "Paused" : "Not started"}</span>}
          </div>
          <p className="mtg-box__text" aria-live="polite">
            {transcript || <span className="mtg-placeholder">Your transcript will appear here as Aidey listens…</span>}
            {interimText && <span className="mtg-para--interim"> {interimText}</span>}
          </p>
        </div>
        <p className="mtg-meta">
          Works best in Chrome or Edge. Aidey listens to whoever is speaking near the device. For meetings on a video call, use the platform's own transcript feature instead.
        </p>
      </>
    ) : (
      <>
        {errorBanner}
        {/* Block 179: in-person on a browser without Web Speech: recording isn't available; say so
            honestly and let them paste instead (the verbatim message points them here). */}
        {platform === "inperson" && !hasWebSpeech && (
          <Banner tone="problem">Live recording is not supported in this browser yet. Use Chrome or Edge to record, or paste your transcript below.</Banner>
        )}
        {/* Paste transcript UI: the platform guide only renders for platforms that have one. */}
        {PLATFORM_GUIDES[platform] && (
          <section className="mtg-card mtg-card--strip" style={{ "--ui-bg": "var(--pastel-blue)" }}>
            <span className="mtg-eyebrow">How to get your transcript</span>
            {guideSteps(PLATFORM_GUIDES[platform].steps.slice(0, 4))}
          </section>
        )}
        <Field label="Transcript">
          <Textarea
            value={pastedText}
            onChange={e => setPastedText(e.target.value)}
            rows={10}
            placeholder={"Paste your meeting transcript here…\n\nYou can also type your own meeting notes. Aidey will find action items like:\n  “Ben will update the report”\n  “The marketing team needs to review the brief”\n  “- [ ] Send proposals – Sarah”"}
          />
        </Field>
      </>
    ),
    foot: canRecordLive ? (
      <>
        {!isListening ? (
          <Button variant="confirm" onClick={startListening}>
            <span aria-hidden="true">🎙️</span> {transcript ? "Resume listening" : "Start listening"}
          </Button>
        ) : (
          <Button variant="secondary" onClick={stopListening}>
            <span aria-hidden="true">⏸</span> Pause
          </Button>
        )}
        {transcript && (
          <Button variant="dark" onClick={() => { stopListening(); setPhase("review"); }}>
            End meeting and review
          </Button>
        )}
      </>
    ) : (
      <Button variant="confirm" onClick={handleGenerateSummary} disabled={pastedText.trim().length < 30 || isAnalysing} aria-busy={isAnalysing || undefined}>
        {isAnalysing ? <><span className="mtg-spin mtg-spin--sm" aria-hidden="true" />Analysing…</> : "Generate summary and tasks"}
      </Button>
    ),
  });

  // ── PHASE: REVIEW (live only: review before summarising) ──────────────────
  if (phase === "review") return shell({
    testId: "meeting-recorder-review",
    title: "Review the transcript",
    back: () => setPhase("record"),
    body: (
      <>
        {errorBanner}
        <Field label="Transcript" hint="Edit the transcript if anything was misheard, then generate your summary.">
          <Textarea
            value={transcript}
            rows={12}
            onChange={e => { setTranscript(e.target.value); finalTranscript.current = e.target.value; }}
          />
        </Field>
      </>
    ),
    foot: (
      <Button variant="confirm" onClick={handleGenerateSummary} disabled={isAnalysing} aria-busy={isAnalysing || undefined}>
        {isAnalysing ? <><span className="mtg-spin mtg-spin--sm" aria-hidden="true" />Analysing…</> : "Generate summary and tasks"}
      </Button>
    ),
  });

  // ── PHASE: SUMMARY ──────────────────────────────────────────────────────────
  if (phase === "summary") return shell({
    testId: "meeting-recorder-summary",
    title: "Meeting summary",
    back: () => setPhase(platform === "inperson" ? "review" : "record"),
    body: (
      <>
        {errorBanner}
        {usedFallback && !error && (
          <Banner tone="notice">Aidey could not reach its summariser, so this is a simpler summary made on this device. No note was saved.</Banner>
        )}

        {/* Overview */}
        <section className="mtg-card mtg-card--strip" style={{ "--ui-bg": "var(--highlight)" }}>
          <span className="mtg-eyebrow">Meeting</span>
          <h3 className="mtg-h">{title || "Untitled meeting"}</h3>
          <p className="mtg-meta">
            <span>{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
            {attendees.length > 0 && <span>{attendees.map(u => u.name).join(", ")}</span>}
            {externalAttendees.length > 0 && (
              <span>{externalAttendees.map(e => e.name).join(", ")} (client)</span>
            )}
          </p>
          {summary?.topics?.length > 0 && (
            <div className="mtg-chips">
              {summary.topics.map(t => <StatusPill key={t} tone="neutral">{t}</StatusPill>)}
            </div>
          )}
        </section>

        {/* Decisions */}
        {summary?.decisions?.length > 0 && (
          <section className="mtg-card mtg-card--strip" style={{ "--ui-bg": "var(--pastel-green)" }}>
            <span className="mtg-eyebrow">Key decisions</span>
            <ul className="mtg-list">
              {summary.decisions.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </section>
        )}

        {/* Action items: editable */}
        <section className="mtg-card">
          <div className="mtg-card__head">
            <span className="mtg-eyebrow">Action items ({actionItems.filter(a => a.keep).length} selected)</span>
            <div className="mtg-row">
              <button type="button" className="mtg-link ui-hit" onClick={() => setActionItems(a => a.map(i => ({ ...i, keep: true })))}>Select all</button>
              <button type="button" className="mtg-link ui-hit" onClick={() => setActionItems(a => a.map(i => ({ ...i, keep: false })))}>Clear all</button>
            </div>
          </div>

          {actionItems.length === 0 && (
            <p className="mtg-text">No action items detected. Try editing the transcript to include phrases like &ldquo;Ben will…&rdquo; or &ldquo;Marketing team to…&rdquo;, or add one below.</p>
          )}

          <div className="mtg-stack" style={{ gap: "var(--space-2)" }}>
            {actionItems.map((item) => (
              <div key={item.id} className={`mtg-item ${item.keep ? "mtg-item--on" : "mtg-item--off"}`}>
                <Checkbox
                  checked={item.keep}
                  onChange={() => setActionItems(a => a.map(x => x.id === item.id ? { ...x, keep: !x.keep } : x))}
                  label={<span className="ui-visually-hidden">Create a task for: {item.text || "this item"}</span>}
                />
                <div className="mtg-item__main">
                  <Input value={item.text} aria-label="Action item" placeholder="What needs doing?"
                    onChange={e => setActionItems(a => a.map(x => x.id === item.id ? { ...x, text: e.target.value } : x))} />
                  <Select
                    value={item.assigneeId || ""}
                    aria-label="Assigned to"
                    onChange={e => {
                      const uid = e.target.value || null; // user ids are UUIDs, do not parseInt
                      const user = uid ? allUsers.find(u => u.id === uid) : null;
                      setActionItems(a => a.map(x => x.id === item.id ? { ...x, assigneeId: uid, assigneeName: user?.name || null, assigneeIds: null } : x));
                    }}
                  >
                    <option value="">Unassigned</option>
                    {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}{u.jobTitle ? ` · ${u.jobTitle}` : ""}</option>)}
                  </Select>
                </div>
              </div>
            ))}
          </div>

          {/* Add action item manually */}
          <div>
            <Button variant="tertiary" size="sm" className="ui-hit"
              onClick={() => setActionItems(a => [...a, { id: a.length, text: "", assigneeId: null, assigneeName: null, keep: true }])}>
              + Add action item
            </Button>
          </div>
        </section>

        {/* Block 179: authoritative contact attribution: the user's pick here wins and is saved to the
            transcript note. Defaults to "No contact"; never inferred. */}
        <section className="mtg-card">
          {contactSelect("Save this meeting to a contact", "The transcript note will be linked to this CRM contact. Leave as “No contact” to skip.")}
          <p className="mtg-meta">Attribution is your choice. Aidey never guesses the contact from what was said.</p>
        </section>

        <p className="mtg-meta">Tasks will appear in each team member's task list immediately. The summary is saved to meeting history.</p>
      </>
    ),
    foot: (
      <>
        <Button variant="secondary" onClick={copySummary}>Copy summary</Button>
        <Button variant="confirm" onClick={handleCreateTasks}>Create tasks and save summary</Button>
      </>
    ),
  });

  // ── PHASE: DONE ─────────────────────────────────────────────────────────────
  if (phase === "done") return shell({
    testId: "meeting-recorder-done",
    title: "Meeting wrapped up",
    close: onClose,
    body: (
      <>
        <div className="mtg-center" style={{ flex: "0 0 auto", paddingBottom: 0 }}>
          <span className="mtg-glyph" aria-hidden="true">✅</span>
          <h3 className="mtg-h">Meeting wrapped up</h3>
          <p className="mtg-text">
            {actionItems.filter(a => a.keep).length} tasks created
            {attendees.length > 1 ? ` and shared with ${attendees.length} attendees` : ""}.
          </p>
        </div>

        <section className="mtg-card">
          <span className="mtg-eyebrow">Tasks created</span>
          <ul className="mtg-stack" style={{ margin: 0, padding: 0, listStyle: "none", gap: "var(--space-2)" }}>
            {actionItems.filter(a => a.keep).map((a, i) => {
              const user = a.assigneeId ? allUsers.find(u => u.id === a.assigneeId) : null;
              return (
                <li key={i} className="mtg-row" style={{ flexWrap: "nowrap", alignItems: "flex-start" }}>
                  {user && <Avatar name={user.name} size={24} color={user.color} />}
                  <span className="mtg-text mtg-text--ink"><strong>{user?.name || "Unassigned"}:</strong> {a.text}</span>
                </li>
              );
            })}
          </ul>
        </section>

        {/* KNOWN LIMITATION: meeting history (transcript + summary) is saved to this
            device's localStorage only, it is NOT synced to the backend, so it won't
            appear on other devices and is lost if the browser cache is cleared. The
            created TASKS are saved to the backend and are unaffected. A backend
            meeting-history table is a future block. */}
        <p className="mtg-meta">
          Your tasks are saved to your account. The full meeting summary is kept on this device only for now, so copy it if you need it elsewhere.
        </p>
      </>
    ),
    foot: (
      <>
        {onClose && <Button variant="tertiary" onClick={onClose}>Close</Button>}
        <Button variant="secondary" onClick={copySummary}>Copy summary to share</Button>
        {setFilter && <Button variant="confirm" onClick={() => setFilter("Today")}>View in Tasks</Button>}
      </>
    ),
  });

  return null;
}
